import type { FC } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import type { AssetFieldType } from "../../../../collaboration/src/shared/types/worksheet";

import { LabeledFileUploadController } from "./LabeledFileUploadController";
import { LabeledSelect } from "./LabeledSelect";
import { LabeledTextInput } from "./LabeledTextInput";

import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";

interface VideoTracksFormProps {
  className?: string;
  defaultTracks?: AssetFieldType["tracks"];
  onSubmit: (data: FormValues) => void;
}

interface FormValues {
  tracks: AssetFieldType["tracks"];
}

const VideoTracksForm: FC<VideoTracksFormProps> = ({ className, defaultTracks, onSubmit }) => {
  const form = useForm<FormValues>({
    defaultValues: {
      tracks:
        defaultTracks && defaultTracks.length
          ? defaultTracks
          : [{ src: "", srcLang: "", label: "", kind: "subtitles" }],
    },
    mode: "onBlur",
  });
  const { register, control } = form;
  const { fields, append, remove } = useFieldArray({
    name: "tracks",
    control,
  });

  return (
    <FormProvider {...form}>
      <form className={className} data-stay-visible onSubmit={form.handleSubmit(onSubmit)}>
        <ol className="flex flex-col gap-4">
          {fields.map((field, index) => {
            return (
              <li className="flex flex-row items-end gap-2" key={field.id}>
                <LabeledTextInput
                  label={t("asset_field.tracks.label")}
                  required
                  {...register(`tracks.${index}.label` as const, {
                    required: t("error.field_required"),
                  })}
                />
                <LabeledFileUploadController
                  accept="text/vtt"
                  label={t("asset_field.tracks.src")}
                  name={`tracks.${index}.src`}
                  required
                />
                <LabeledTextInput
                  label={t("asset_field.tracks.srcLang")}
                  required
                  {...register(`tracks.${index}.srcLang` as const, {
                    required: t("error.field_required"),
                  })}
                  className="w-20"
                />
                <LabeledSelect
                  label={t("asset_field.tracks.kind")}
                  required
                  {...register(`tracks.${index}.kind` as const, {
                    required: t("error.field_required"),
                    validate: (value) =>
                      ["subtitles", "captions", "descriptions"].includes(value as string),
                  })}
                >
                  <option value="subtitles">{t("asset_field.tracks.subtitles")}</option>
                  <option value="captions">{t("asset_field.tracks.captions")}</option>
                  <option value="descriptions">{t("asset_field.tracks.descriptions")}</option>
                </LabeledSelect>
                <SquareButton kind="destructive" size="m" onClick={() => remove(index)}>
                  {t("common.delete")}
                </SquareButton>
              </li>
            );
          })}
        </ol>
        <div className="mt-4 flex flex-row gap-2">
          <SquareButton
            kind="secondary"
            size="m"
            onClick={() => append({ src: "", srcLang: "", label: "", kind: "subtitles" })}
          >
            {t("asset_field.tracks.add_track")}
          </SquareButton>
          <SquareButton kind="success" size="m" type="submit">
            {t("common.save")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};

// Using a default export to allow lazy loading
// eslint-disable-next-line import/no-default-export
export default VideoTracksForm;
